import { isThemeId, type ThemeId } from "../theme/themeCatalog";

const QUERY_SPLIT_LIMIT = 2;
const EMBED_ENABLED_VALUE = "1";

/**
 * Einbettung öffentlicher Seiten in eine fremde Seite (z. B. die Tarifauswahl auf mqorva.de):
 * `…#/tariffs?embed=1&theme=web-dark`. Eingebettet entfällt der eigene Seitenrahmen, die Fläche bleibt
 * durchsichtig, die Höhe wird der einbettenden Seite gemeldet, und Folgeschritte (Konto, Anfrage)
 * öffnen sich als ganze Seite – Anmeldung und Zahlung funktionieren in einem fremden Rahmen nicht verlässlich.
 */
export type EmbedRequest = { embedded: boolean; themeId: ThemeId | null };

/** Nachricht an die einbettende Seite mit der benötigten Höhe in CSS-Pixeln. */
export const EMBED_HEIGHT_MESSAGE = "mqorva:embed-height";

function readParams(): URLSearchParams[] {
  if (typeof window === "undefined") return [];
  return [new URLSearchParams(window.location.hash.split("?", QUERY_SPLIT_LIMIT)[1] ?? ""), new URLSearchParams(window.location.search)];
}

export function readEmbedRequest(): EmbedRequest {
  const params = readParams();
  const embedded = params.some((p) => p.get("embed") === EMBED_ENABLED_VALUE) && typeof window !== "undefined" && window.parent !== window;
  if (!embedded) return { embedded: false, themeId: null };
  const theme = params.map((p) => p.get("theme")).find((value) => value != null);
  return { embedded, themeId: isThemeId(theme) ? theme : null };
}

/**
 * Markiert das Dokument als eingebettet (durchsichtige Fläche, `ui/base.css`) und meldet jede Höhenänderung
 * des Inhalts. Gemessen wird der Inhalt selbst, nicht `body` – der ist mindestens so hoch wie der Rahmen
 * und könnte ihn nie wieder verkleinern.
 */
export function startEmbedFrame(content: HTMLElement): () => void {
  if (typeof document === "undefined") return () => undefined;
  const root = document.documentElement;
  root.dataset.embedded = "true";
  let last = 0;
  const report = () => {
    const height = Math.ceil(content.getBoundingClientRect().height);
    if (height === last) return;
    last = height;
    window.parent.postMessage({ type: EMBED_HEIGHT_MESSAGE, height }, "*");
  };
  const observer = new ResizeObserver(report);
  observer.observe(content);
  report();
  return () => {
    observer.disconnect();
    delete root.dataset.embedded;
  };
}

/** Öffnet ein Ziel als ganze Seite statt im Rahmen (nur aus einem Klick heraus). */
export function openOutsideFrame(href: string) {
  window.open(new URL(href, window.location.href).toString(), "_top");
}
